import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocaleProvider, type SupportedLocale } from '@parentpath/i18n';
import { AppLayout, AdaptiveLayout } from '@/components/layout';
import { OnboardingGuard, PregnancyRequiredGuard, PregnancyOnlyGuard } from '@/components/guards';
import { useDeepLinks } from '@/hooks/useDeepLinks';
import { useUserActivity } from '@/hooks/useUserActivity';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/lib/supabase';
import { ThemeProvider } from '@/contexts/ThemeContext';
import HomePage from '@/pages/HomePage';
import ProfilePage from '@/pages/ProfilePage';
import GDPRRequestPage from '@/pages/GDPRRequestPage';
import {
  AuthProvider,
  useAuth,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  ResetPasswordConfirmPage,
  AuthCallbackPage,
  AuthConfirmPage,
  ProtectedRoute,
  PublicRoute,
} from '@/features/auth';
import { OnboardingPage } from '@/features/onboarding';
import { InvitePage, PartnerPage, RealtimeProvider } from '@/features/partner';
import { SSWPage } from '@/features/ssw';
import { NameSwipePage, MatchListPage, MySuggestionsPage, MyLikesPage } from '@/features/names';
import { ChecklistsPage, ChecklistDetailPage } from '@/features/checklists';
import { CalendarPage } from '@/features/calendar';
import { AIChatPage } from '@/features/ai-chat';
import { ToolsPage } from '@/pages/ToolsPage';
import { SubscriptionDetailsPage, SubscriptionSuccessPage, useRevenueCatInit } from '@/features/subscription';
import { ScannerPage, ProductDetailPage, ScanHistoryPage } from '@/features/scanner';
import { ContractionTimerPage } from '@/features/contraction-timer';
import { HelpPage, CategoryPage, ContactPage, FeedbackPage } from '@/features/help';
import { GuidesPage, GlossarListPage, GlossarDetailPage, FAQListPage, FAQDetailPage } from '@/features/guides';
import { BlogListPage, BlogDetailPage } from '@/features/blog';
import { ImprintPage, PrivacyPage, TermsPage } from '@/features/legal';
import { NotificationsPage, NotificationSettingsPage } from '@/features/notifications';
import { PregnancyInfoPage } from '@/features/pregnancy-info';
import { ActivityFeedPage } from '@/features/activity-feed';
import { BabyCarePage, BabyCareStatsPage, MedicationPlanPage } from '@/features/baby-care';
import { BabyGrowthPage } from '@/features/baby-growth';
import { BabyDevelopmentPage } from '@/features/baby-development';
import { SleepSoundsPage } from '@/features/sleep-sounds';
import { SleepPlanPage, SleepAnalysisPage } from '@/features/sleep-plan';
import { GoogleAnalytics } from '@/components/analytics';
import { ConsentGate } from '@/features/consent';
import { SmartAppBanner } from '@/components/SmartAppBanner';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

// Inner component to use hooks that require router context
function AppRoutes() {
  // Initialize deep link handling for iOS/Android
  useDeepLinks();

  // Initialize RevenueCat for native IAP (iOS/Android)
  useRevenueCatInit();

  // Report the user as active (app start + return to foreground).
  // Feeds profiles.last_seen_at / .platform and user_activity_days (167).
  useUserActivity();

  return (
    <Routes>
      {/* Public Routes (redirect if logged in) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/confirm"
        element={<ResetPasswordConfirmPage />}
      />

      {/* Auth Callback (OAuth / PKCE / implicit hash) */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Email confirmation (token_hash from Supabase email templates) */}
      <Route path="/auth/confirm" element={<AuthConfirmPage />} />

      {/* Chat Page (with custom layout - has fixed header/input) */}
      {/* Requires active pregnancy or premium subscription */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <PregnancyRequiredGuard>
                <AIChatPage />
              </PregnancyRequiredGuard>
            </OnboardingGuard>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes with Layout - requires completed onboarding */}
      <Route
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <AppLayout />
            </OnboardingGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/ssw" element={<PregnancyRequiredGuard><SSWPage /></PregnancyRequiredGuard>} />
        <Route path="/baby" element={<PregnancyRequiredGuard><BabyDevelopmentPage /></PregnancyRequiredGuard>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/tools" element={<PregnancyRequiredGuard><ToolsPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/names" element={<PregnancyOnlyGuard><NameSwipePage /></PregnancyOnlyGuard>} />
        <Route path="/tools/names/matches" element={<PregnancyOnlyGuard><MatchListPage /></PregnancyOnlyGuard>} />
        <Route path="/tools/names/suggestions" element={<PregnancyOnlyGuard><MySuggestionsPage /></PregnancyOnlyGuard>} />
        <Route path="/tools/names/likes" element={<PregnancyOnlyGuard><MyLikesPage /></PregnancyOnlyGuard>} />
        <Route path="/tools/checklists" element={<PregnancyRequiredGuard><ChecklistsPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/checklists/:id" element={<PregnancyRequiredGuard><ChecklistDetailPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/guides" element={<PregnancyRequiredGuard><GuidesPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/guides/glossar" element={<PregnancyRequiredGuard><GlossarListPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/guides/glossar/:slug" element={<PregnancyRequiredGuard><GlossarDetailPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/guides/fragen" element={<PregnancyRequiredGuard><FAQListPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/guides/fragen/:slug" element={<PregnancyRequiredGuard><FAQDetailPage /></PregnancyRequiredGuard>} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/tools/scanner" element={<PregnancyRequiredGuard><ScannerPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/scanner/product/:barcode" element={<PregnancyRequiredGuard><ProductDetailPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/scanner/history" element={<PregnancyRequiredGuard><ScanHistoryPage /></PregnancyRequiredGuard>} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/tools/contractions" element={<PregnancyOnlyGuard><ContractionTimerPage /></PregnancyOnlyGuard>} />
        <Route path="/tools/baby-care" element={<PregnancyRequiredGuard><BabyCarePage /></PregnancyRequiredGuard>} />
        <Route path="/tools/baby-care/stats" element={<PregnancyRequiredGuard><BabyCareStatsPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/baby-care/medications" element={<PregnancyRequiredGuard><MedicationPlanPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/growth" element={<PregnancyRequiredGuard><BabyGrowthPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/sleep-sounds" element={<PregnancyRequiredGuard><SleepSoundsPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/sleep-plan" element={<PregnancyRequiredGuard><SleepPlanPage /></PregnancyRequiredGuard>} />
        <Route path="/tools/sleep-plan/analysis" element={<PregnancyRequiredGuard><SleepAnalysisPage /></PregnancyRequiredGuard>} />
        <Route path="/profile/notifications" element={<NotificationSettingsPage />} />
        <Route path="/profile/pregnancy-info" element={<PregnancyInfoPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/activity" element={<ActivityFeedPage />} />
        <Route path="/partner" element={<PartnerPage />} />
        <Route path="/subscription" element={<SubscriptionDetailsPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/help/feedback" element={<FeedbackPage />} />
        <Route path="/settings" element={<SettingsPlaceholder />} />
      </Route>

      {/* Onboarding (protected but without main layout) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Partner Invite (public - redirects to login if needed) */}
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* ============================================= */}
      {/* PUBLIC PAGES (with AdaptiveLayout)           */}
      {/* - Authenticated: shows full AppLayout        */}
      {/* - Unauthenticated: shows minimal PublicLayout */}
      {/* ============================================= */}

      {/* Legal Pages - publicly accessible */}
      <Route element={<AdaptiveLayout />}>
        <Route path="/legal/imprint" element={<ImprintPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
      </Route>

      {/* Help Pages - publicly accessible */}
      <Route element={<AdaptiveLayout />}>
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/category/:slug" element={<CategoryPage />} />
        <Route path="/help/contact" element={<ContactPage />} />
      </Route>

      {/* GDPR Request Page - publicly accessible */}
      <Route element={<AdaptiveLayout />}>
        <Route path="/profile/gdpr" element={<GDPRRequestPage />} />
        <Route path="/gdpr" element={<GDPRRequestPage />} />
      </Route>
    </Routes>
  );
}

/** Wraps children with locale provider. Locale switching is enabled if:
 *  - The user is an admin, OR
 *  - English is publicly activated in supported_locales (toggled from admin panel)
 *
 * While loading the enabled-status, `enabled` is passed as `undefined` so the
 * LocaleProvider does NOT prematurely force 'de' and overwrite localStorage.
 */
function LocaleGate({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const { user } = useAuth();
  const [isPubliclyEnabled, setIsPubliclyEnabled] = useState(false);
  const [publicCheckDone, setPublicCheckDone] = useState(false);
  const [userLocale, setUserLocale] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('supported_locales')
      .select('is_active')
      .eq('code', 'en')
      .single()
      .then(({ data }) => {
        if (data?.is_active) setIsPubliclyEnabled(true);
        setPublicCheckDone(true);
      });
  }, []);

  // Fetch user's preferred locale and timezone from profile
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('preferred_locale, timezone')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.preferred_locale) {
          setUserLocale(data.preferred_locale);
        }
        // Backfill timezone for existing users who don't have one yet
        // (still on the default 'Europe/Berlin' from migration)
        if (data && !data.timezone) {
          const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (detectedTz) {
            supabase
              .from('profiles')
              .update({ timezone: detectedTz })
              .eq('id', user.id)
              .then(() => {});
          }
        }
      });
  }, [user?.id]);

  // Save locale changes to profile
  const handleLocaleChange = useCallback(async (locale: SupportedLocale) => {
    if (!user?.id) return;
    await supabase
      .from('profiles')
      .update({ preferred_locale: locale } as never)
      .eq('id', user.id);
  }, [user?.id]);

  // Don't pass a definitive enabled=false while still loading – that would
  // force 'de' and overwrite the localStorage cache before the DB value arrives.
  const isCheckComplete = !isAdminLoading && publicCheckDone;
  const enabled = isCheckComplete ? (isAdmin || isPubliclyEnabled) : undefined;

  return (
    <LocaleProvider
      enabled={enabled}
      userLocale={userLocale}
      onLocaleChange={handleLocaleChange}
    >
      {children}
    </LocaleProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <LocaleGate>
              <RealtimeProvider>
                <GoogleAnalytics />
                <SmartAppBanner />
                {/* Einwilligungs-Gate auf App-Ebene, nicht in AppLayout:
                    /chat und /onboarding laufen an AppLayout vorbei, und
                    AdaptiveLayout wuerde die Rechtstexte hinter dem Gate
                    verstecken. Die Ausnahmen stehen in consentPaths.ts. */}
                <ConsentGate />
                <AppRoutes />
              </RealtimeProvider>
            </LocaleGate>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Placeholder components for future implementation
function SettingsPlaceholder() {
  return (
    <div className="text-center py-12">
      <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
      <p className="text-gray-500 mt-2">Coming soon</p>
    </div>
  );
}

export default App;
